import { IntlShape } from 'react-intl';

/**
 * All these translations are based on a key-value map.
 * Each key is composed from the sport id, source offer type id and source offer type input source id
 * All the above id's can be found on the event object and in the offer object that comes from the BE
 *
 * The values are a function that handles the translation of the offers
 *
 * The compound key helps us knowing the pattern of the market, so that we translated only he key words and not the dynamic values
 *
 * Key Template: sportId_sourceOfferTypeId_sourceOfferTypeInputSourceId
 * Important Note - sportId must be the new one, not the legacy one
 */

export const getOfferTranslatedOfferName = (key: string, offerName: string, intl: IntlShape) => {
  const wildCard = offerWildCards[key];
  return wildCard ? wildCard(intl, offerName) : offerName;
};

export const createCompoundKey = (sportId: number, sourceOfferTypeId: string, sourceOfferTypeInputSourceId: number) =>
  `${sportId}_${sourceOfferTypeId}_${sourceOfferTypeInputSourceId}`;

export interface OffersTranslations {
  [key: string]: (intl: IntlShape, outcomeName: string) => string;
}

const outrightOfferWildCards = {
  '1_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '1_535_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '1_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '5_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '5_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '6_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '3_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '3_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '4_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '4_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '4_535_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '8_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '8_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '7_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '7_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '12_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '12_535_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '9_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '16_534_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '16_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '18_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '18_559_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '17_535_2': (_: IntlShape, outcomeName: string) => outcomeName,
  '17_536_2': (_: IntlShape, outcomeName: string) => outcomeName,
};

const simpleOfferWildCards = {
  '1_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.fulltime_result' }),
  '1_18_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_goals' }),
  '3_186_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner' }),
  '4_685_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.man_of_the_match' }),
  '4_648_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.most_sixes' }),
  '5_225_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_points_incl_overtime' }),
  '5_219_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.money_line' }),
  '13_1_3': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'common.winner' }),
  '7_60_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_result' }),
  '7_173_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_pts' }),
  '8_384_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_180s' }),
  '8_186_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner' }),
  '9_186_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner' }),
  '9_494_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_frames' }),
  '10_186_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner' }),
  '10_911_2': (intl: IntlShape, _: string) => `${intl.formatMessage({ id: 'offer.go_the_distance' })}?`,
  '11_186_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner' }),
  '11_911_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.go_the_distance' }),
  '12_18_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_goals' }),
  '12_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.regular_time_result' }),
  '2_1_3': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'common.to_win' }),
  '4_640_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_sixes' }),
  '4_342_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_a_tie' }),
  '4_639_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_fours' }),
  '4_682_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_in_the_highest_scoring_over' }),
  '4_647_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.most_fours' }),
  '1_60_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_result' }),
  '1_66_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_handicap' }),
  '1_10_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance' }),
  '1_14_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap_result' }),
  '1_68_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total_goals' }),
  '1_11_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.draw_no_bet' }),
  '1_16_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap' }),
  '8_381_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.most_180s' }),
  '1_37_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.ft_result_and_total_goals' }),
  '1_35_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.ft_result_and_btts' }),
  '1_29_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.both_teams_to_score' }),
  '1_45_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score' }),
  '1_199_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score' }),
  '1_166_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_corners' }),
  '1_888_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.anytime_goalscorer_and_ft_result' }),
  '1_889_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.anytime_goalscorer_and_correct_score' }),
  '1_165_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.corner_handicap' }),
  '1_162_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.most_corners' }),
  '1_40_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.anytime_goalscorer' }),
  '4_340_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner_incl_super_over' }),
  '1_47_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_fulltime' }),
  '1_7_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_wins_the_rest_of_the_match' }),
  '12_16_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap' }),
  '12_11_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.draw_no_bet' }),
  '12_37_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.rt_result_total_goals' }),
  '12_7_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_wins_the_rest_of_the_match' }),
  '12_14_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap_result' }),
  '12_10_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance' }),
  '12_21_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.exact_goals' }),
  '5_16_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap' }),
  '5_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.fulltime_result' }),
  '5_18_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'common.total' }),
  '5_15_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin' }),
  '4_11_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.draw_no_bet' }),
  '4_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner_3_way' }),
  '4_698_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.team_with_top_batter' }),
  '1_86_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_draw_no_bet' }),
  '1_46_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_fulltime_correct_score' }),
  '1_39_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.last_goalscorer' }),
  '1_81_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_correct_score' }),
  '1_90_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_total_goals' }),
  '1_88_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_handicap' }),
  '1_36_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_goals_and_btts' }),
  '1_30_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_to_score' }),
  '1_52_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.highest_scoring_half' }),
  '1_25_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.goal_range' }),
  '1_83_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_result' }),
  '1_65_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_handicap_result' }),
  '1_74_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_odd_even' }),
  '1_26_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_total_goals' }),
  '1_75_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_btts' }),
  '1_881_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.any_team_to_win' }),
  '5_223_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.spread_incl_ot' }),
  '1_136_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.most_bookings' }),
  '1_9_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.last_goal' }),
  '1_15_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin' }),
  '5_220_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_overtime' }),
  '5_290_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin_incl_ot' }),
  '1_177_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total_corners' }),
  '1_173_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_most_corners' }),
  '1_139_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_bookings' }),
  '14_186_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'common.winner' }),
  '1_169_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.corner_range' }),
  '1_138_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_booking_points' }),
  '1_146_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.sending_off' }),
  '1_183_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_odd_even_corners' }),
  '1_546_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance_and_both_teams_to_score' }),
  '1_899_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_to_score_3_+' }),
  '1_547_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance_and_total' }),
  '1_158_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_booking_point_range' }),
  '1_85_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_double_chance' }),
  '1_172_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_corners' }),
  '1_64_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_draw_no_bet' }),
  '1_544_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_1x2_and_total' }),
  '1_543_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_1x2_and_both_teams_to_score' }),
  '1_145_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.booking_point_range' }),
  '1_61_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_which_team_wins_the_rest' }),
  '1_176_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_corner_handicap' }),
  '1_152_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total_bookings' }),
  '1_151_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total_booking_points' }),
  '1_95_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_btts' }),
  '1_79_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_result_and_1st_half_total_goals' }),
  '1_63_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_double_chance' }),
  '7_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.fulltime_result' }),
  '1_898_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_to_score_2_+' }),
  '6_225_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_points_incl_overtime' }),
  '1_71_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_exact_goals' }),
  '1_142_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.exact_bookings' }),
  '1_78_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_result_and_btts' }),
  '1_21_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.exact_goals' }),
  '1_182_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_corner_range' }),
  '6_219_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.money_line' }),
  '11_910_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_method' }),
  '11_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner_3_way' }),
  '1_159_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_sending_off' }),
  '7_47_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_fulltime' }),
  '7_18_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_points' }),
  '7_471_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_point_range' }),
  '7_11_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.draw_no_bet' }),
  '5_229_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_incl_ot' }),
  '7_475_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.try_draw_no_bet' }),
  '7_64_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_draw_no_bet' }),
  '3_199_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score' }),
  '4_717_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.batter_head_2_head_handicap' }),
  '11_912_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winner_and_exact_rounds' }),
  '11_18_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_rounds' }),
  '1_122_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_a_penalty_shootout' }),
  '1_112_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.overtime_and_goal' }),
  '1_2_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.to_qualify' }),
  '1_220_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_extra_time' }),
  '7_484_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_try_draw_no_bet' }),
  '1_5_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_method' }),
  '4_694_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_wins_the_coin_toss' }),
  '9_495_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_frames' }),
  '9_493_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.frame_handicap' }),
  '9_492_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_a_deciding_frame' }),
  '9_491_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_player_wins_the_rest_of_the_match' }),
  '1_820_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_fulltime_and_exact_goals' }),
  '1_94_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_odd_even' }),
  '1_818_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_fulltime_and_total' }),
  '1_864_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.draw_or_any_clean_sheet' }),
  '1_819_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_fulltime_and_1st_half_total' }),
  '1_93_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_exact_goals' }),
  '1_98_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_correct_score' }),
  '1_551_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.multiscores' }),
  '1_541_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance_match_and_2nd_half_both_teams_score' }),
  '1_540_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance_match_and_1st_half_both_teams_score' }),
  '1_553_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_multigoals' }),
  '1_542_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_double_chance_and_both_teams_to_score' }),
  '1_548_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.multigoals' }),
  '1_55_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_2nd_half_both_teams_to_score' }),
  '1_552_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_multigoals' }),
  '1_861_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.draw_or_btts' }),
  '1_545_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_double_chance_and_both_teams_to_score' }),
  '1_780_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_tackles' }),
  '1_776_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_shots' }),
  '1_777_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_shots_on_goal' }),
  '1_775_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_goals' }),
  '1_778_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_passes' }),
  '1_770_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_assists' }),
  '1_164_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.last_corner' }),
  '1_149_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_most_bookings' }),
  '1_175_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_last_corner' }),
  '1_155_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_exact_bookings' }),
  '9_199_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score' }),
  '4_699_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.team_with_top_bowler' }),
  '5_292_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.money_line_and_total_points_incl_ot' }),
  '5_234_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.highest_scoring_quarter' }),
  '5_60_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_result' }),
  '5_66_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_spread' }),
  '5_68_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total_points' }),
  '4_702_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_batter_total_runs' }),
  '3_189_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_games' }),
  '8_188_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.set_handicap' }),
  '12_406_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winner_incl_ot_and_pen' }),
  '4_654_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_run_outs' }),
  '10_18_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_rounds' }),
  '10_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner_3_way' }),
  '10_913_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winner_and_round_range' }),
  '10_912_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winner_and_exact_rounds' }),
  '10_910_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_method' }),
  '4_696_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_wides' }),
  '4_655_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_extras' }),
  '4_703_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.rabbit_total' }),
  '4_695_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_ducks' }),
  '4_710_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_wins_the_coin_tss_and_the_match' }),
  '3_187_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.game_handicap' }),
  '3_188_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.set_handicap' }),
  '3_196_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.exact_sets' }),
  '3_198_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_games' }),
  '3_201_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_result_1st_set_match' }),
  '3_1055_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner_and_total_games' }),
  '3_195_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_a_tiebreak' }),
  '3_314_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_sets' }),
  '3_194_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.any_set_to_nil' }),
  '12_220_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_overtime' }),
  '12_35_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.rt_result_and_btts' }),
  '12_431_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score' }),
  '12_199_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score' }),
  '12_30_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_to_score' }),
  '12_432_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.highest_scoring_period' }),
  '12_26_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even' }),
  '12_29_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.both_teams_to_score' }),
  '12_15_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin' }),
  '12_420_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_to_score_incl_overtime_and_penalties' }),
  '12_412_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_incl_overtime_and_penalties' }),
  '12_424_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1x2_and_total_incl_overtime_and_penalties' }),
  '12_425_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1x2_and_both_teams_to_score_incl_overtime_and_penalties' }),
  '12_409_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin_incl_overtime_an_penalties' }),
  '12_416_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.exact_goals_icl_overtime_and_penalties' }),
  '12_419_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_incl_overtime_and_penalties' }),
  '12_427_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score_incl_overtime_and_penalties' }),
  '12_410_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap_incl_overtime_and_penalties' }),
  '12_9_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.last_goal' }),
  '6_223_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.spread_incl_ot' }),
  '6_47_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.halftime_fulltime' }),
  '6_229_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_incl_overtime' }),
  '6_52_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.highest_scoring_half' }),
  '6_290_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin_incl_ot' }),
  '6_220_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.will_there_be_overtime' }),
  '6_234_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.highest_scoring_quarter' }),
  '6_64_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_draw_no_bet' }),
  '6_66_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_spread' }),
  '6_10_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance' }),
  '6_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.fulltime_result' }),
  '6_11_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.draw_no_bet' }),
  '6_74_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_odd_even' }),
  '6_68_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total_points' }),
  '6_60_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_result' }),
  '6_905_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_next_score' }),
  '6_903_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.next_score_incl_ot' }),
  '6_904_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.next_scoring_type_incl_ot' }),
  '6_26_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_points' }),
  '6_231_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_spread_incl_ot' }),
  '6_88_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_handicap' }),
  '6_232_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_total_points_incl_ot' }),
  '6_293_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_1x2_incl_overtime' }),
  '6_294_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_draw_no_bet_incl_overtime' }),
  '6_83_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_result' }),
  '6_610_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.fulltime_result_incl_ot' }),
  '6_767_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_receptions' }),
  '6_765_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_receiving_yards' }),
  '6_764_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_rushing_yards' }),
  '6_761_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_passing_touchdowns' }),
  '6_759_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_passing_yards' }),
  '6_762_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_carries' }),
  '6_760_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_pass_completion' }),
  '6_763_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_rushing_touchdowns' }),
  '6_766_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_receiving_touchdowns' }),
  '8_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner_3_way' }),
  '7_15_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin' }),
  '7_10_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.double_chance' }),
  '7_26_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_points' }),
  '7_14_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap_result' }),
  '7_63_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_double_chance' }),
  '7_470_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_winning_margin' }),
  '7_74_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_odd_even' }),
  '7_16_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap' }),
  '7_66_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_handicap' }),
  '7_52_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.highest_scoring_half' }),
  '7_37_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.fulltime_and_total_points' }),
  '7_486_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_try_handicap' }),
  '7_65_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_handicap_result' }),
  '7_476_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.try_handicap_result' }),
  '7_478_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_tries' }),
  '7_477_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.try_handicap' }),
  '7_298_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.point_range' }),
  '7_487_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total_tries' }),
  '7_473_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.try_result' }),
  '7_481_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_tries' }),
  '7_482_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_try_result' }),
  '7_474_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.try_double_chance' }),
  '7_485_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_try_handicap_result' }),
  '7_483_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_try_double_chance' }),
  '7_490_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_odd_even_tries' }),
  '5_90_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_total_points' }),
  '5_88_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_spread' }),
  '5_298_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.point_range' }),
  '5_83_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.2nd_half_result' }),
  '6_932_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_field_goals_made_incl_ot' }),
  '6_931_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_touchdowns_incl_ot' }),
  '6_934_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_sacks_incl_overtime' }),
  '6_933_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_turnovers_incl_overtime' }),
  '9_1_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.match_winner_3_way' }),
  '1_116_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.extra_time_total_goals' }),
  '1_119_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.extra_time_1st_half_1x2' }),
  '1_113_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.extra_time_result' }),
  '8_383_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.180s_handicap' }),
  '1_123_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.penalty_shootout_winner' }),
  '5_289_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_wins_the_jump_ball' }),
  '7_3_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_will_win_the_final' }),
  '1_3_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_will_win_the_final' }),
  '7_2_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.to_qualify' }),
  '1_893_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.last_goalscorer' }),
  '12_113_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.overtime_1x2' }),
  '12_116_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.overtime_total' }),
  '7_113_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.overtime_1x2' }),
  '8_532_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score_in_sets' }),
  '8_314_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_sets' }),
  '8_367_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_legs' }),
  '8_366_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.leg_handicap' }),
  '8_368_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.odd_even_sets' }),
  '7_68_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_total' }),
  '1_127_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.penalty_shootout_total' }),
  '7_7_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_wins_the_rest_of_the_match' }),
  '1_4_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_will_win_the_3rd_place_final' }),
  '7_4_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.which_team_will_win_the_3rd_place_final' }),
  '12_968_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.anytime_goalscorer' }),
  '12_969_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.player_to_score_a_point' }),
  '7_61_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.1st_half_which_team_wins_the_rest' }),
  '15_251_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winner_incl_extra_innings' }),
  '15_256_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.handicap_incl_extra_innings' }),
  '15_258_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_incl_extra_innings' }),
  '14_237_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.point_handicap' }),
  '14_238_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_points' }),
  '16_186_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'common.winner' }),
  '16_238_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_points' }),
  '16_237_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.point_handicap' }),
  '16_199_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.correct_score' }),
  '18_539_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.head2head' }),
  '18_1100_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_overtakings' }),
  '18_1091_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.podium' }),
  '18_1097_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.grid_position_range_of_the_winner' }),
  '18_1099_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_finishers' }),
  '18_1110_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'common.winner' }),
  '18_1092_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winner_of_group' }),
  '18_1093_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.fastest_lap' }),
  '18_15_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.winning_margin' }),
  '17_537_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.3_ball' }),
  '17_1005_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.ball_3' }),
  '17_1016_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_eagles' }),
  '17_1018_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_pars' }),
  '17_1017_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_birdies' }),
  '17_1019_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'offer.total_bogeys' }),
  '17_1159_2': (intl: IntlShape, _: string) => intl.formatMessage({ id: 'common.winner' }),
};

const complexOfferWildCards = {
  '3_202_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Set - Winner', intl.formatMessage({ id: 'offer.set_winner' })),
  '3_218_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game', intl.formatMessage({ id: 'common.game' }))
      .replace('Point - Winner', intl.formatMessage({ id: 'offer.point_winner' })),
  '4_362_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Delivery', intl.formatMessage({ id: 'offer.delivery' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_363_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Delivery', intl.formatMessage({ id: 'offer.delivery' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '8_375_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('Winner', intl.formatMessage({ id: 'common.winner' })),
  '4_662_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Inns', intl.formatMessage({ id: 'offer.inns' })).replace('To Score', intl.formatMessage({ id: 'offer.to_score' })),
  '4_651_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Dismissal Method', intl.formatMessage({ id: 'offer.dismissal_method' })),
  '4_816_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Dismissal Method (extended)', intl.formatMessage({ id: 'offer.dismissal_method_extended' })),
  '4_658_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Fours', intl.formatMessage({ id: 'offer.total_fours' })),
  '4_643_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Fours', intl.formatMessage({ id: 'offer.total_fours' })),
  '4_356_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_660_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Sixes', intl.formatMessage({ id: 'offer.total_sixes' })),
  '4_638_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_877_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_359_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Odd/Even', intl.formatMessage({ id: 'offer.odd_even' })),
  '4_666_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Extras', intl.formatMessage({ id: 'offer.total_extras' })),
  '4_360_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Odd/Even', intl.formatMessage({ id: 'offer.odd_even' })),
  '4_878_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_659_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Fours', intl.formatMessage({ id: 'offer.total_fours' })),
  '4_357_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_353_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Overs 0 To', intl.formatMessage({ id: 'offer.inns_overs_0_to' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_661_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Sixes', intl.formatMessage({ id: 'offer.total_sixes' })),
  '4_352_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Overs 0 To', intl.formatMessage({ id: 'offer.inns_overs_0_to' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_875_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total At', intl.formatMessage({ id: 'offer.total_at' }))
      .replace('Dismissal', intl.formatMessage({ id: 'offer.dismissal' })),
  '4_670_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total In The Highest Scoring Over', intl.formatMessage({ id: 'offer.total_in_the_highest_scoring_over' })),
  '4_351_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Overs 0 To', intl.formatMessage({ id: 'offer.inns_overs_0_to' }))
      .replace('Result', intl.formatMessage({ id: 'offer.result' })),
  '4_646_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Team With Highest Score At', intl.formatMessage({ id: 'offer.team_with_highest_score_at' }))
      .replace('Dismissal', intl.formatMessage({ id: 'offer.dismissal' })),
  '4_645_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Result', intl.formatMessage({ id: 'offer.result' })),
  '4_671_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total In The Highest Scoring Over', intl.formatMessage({ id: 'offer.total_in_the_highest_scoring_over' })),
  '4_876_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total At', intl.formatMessage({ id: 'offer.total_at' }))
      .replace('Dismissal', intl.formatMessage({ id: 'offer.dismissal' })),
  '4_817_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Dismissal Method (extended)', intl.formatMessage({ id: 'offer.dismissal_method_extended' })),
  '4_667_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Extras', intl.formatMessage({ id: 'offer.total_extras' })),
  '1_20_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '1_19_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '8_371_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Leg Handicap', intl.formatMessage({ id: 'offer.set_leg_handicap' })),
  '8_392_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('To Score A 180', intl.formatMessage({ id: 'offer.to_score_a_180' })),
  '8_374_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Correct Score (in Legs)', intl.formatMessage({ id: 'offer.set_correct_score_in_legs' })),
  '8_373_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Odd/Even Legs', intl.formatMessage({ id: 'offer.set_odd_even_legs' })),
  '8_393_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('To Score A 180', intl.formatMessage({ id: 'offer.to_score_a_180' })),
  '8_372_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Total Legs', intl.formatMessage({ id: 'offer.set_total_legs' })),
  '8_379_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('Checkout Score', intl.formatMessage({ id: 'offer.checkout_score' })),
  '8_380_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('Checkout Colour', intl.formatMessage({ id: 'offer.checkout_colour' })),
  '8_385_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total 180s', intl.formatMessage({ id: 'offer.total_180s' })),
  '8_370_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Which Player Wins The Rest', intl.formatMessage({ id: 'offer.set_which_player_wins_the_rest' })),
  '8_376_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('Total Darts', intl.formatMessage({ id: 'offer.total_darts' })),
  '8_378_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('Point Range On', intl.formatMessage({ id: 'offer.point_range_on' }))
      .replace('Visit', intl.formatMessage({ id: 'offer.visit' })),
  '8_386_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total 180s', intl.formatMessage({ id: 'offer.total_180s' })),
  '8_391_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Leg', intl.formatMessage({ id: 'offer.leg' }))
      .replace('Any Player To Score A 180', intl.formatMessage({ id: 'offer.any_player_to_score_180' })),
  '3_204_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Total Games', intl.formatMessage({ id: 'offer.set_total_games' })),
  '3_210_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game', intl.formatMessage({ id: 'common.game' }))
      .replace('Winner', intl.formatMessage({ id: 'common.winner' })),
  '3_207_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Correct Score', intl.formatMessage({ id: 'offer.set_correct_score' })),
  '1_891_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Goalscorer & 1x2', intl.formatMessage({ id: 'offer.goalscorer_and_1x2' })),
  '1_892_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Goalscorer', intl.formatMessage({ id: 'offer.goalscorer' })),
  '1_38_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Goalscorer', intl.formatMessage({ id: 'offer.goalscorer' })),
  '1_890_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Goalscorer & Correct Score', intl.formatMessage({ id: 'offer.goalscorer_and_correct_score' })),
  '12_458_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Which Team Wins The Rest', intl.formatMessage({ id: 'offer.period_which_team_wins_the_rest' })),
  '12_446_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Total Goals', intl.formatMessage({ id: 'offer.period_total_goals' })),
  '12_19_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '12_451_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Period', intl.formatMessage({ id: 'common.period' }))
      .replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '12_448_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Period', intl.formatMessage({ id: 'common.period' }))
      .replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '12_460_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Handicap', intl.formatMessage({ id: 'offer.period_handicap' })),
  '12_429_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period 1x2 & 1x2', intl.formatMessage({ id: 'offer.period_1x2_and_1x2' })),
  '12_8_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '12_20_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '12_445_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Handicap', intl.formatMessage({ id: 'offer.period_handicap' })),
  '12_13_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('No Bet', intl.formatMessage({ id: 'offer.no_bet' })),
  '12_24_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '12_12_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('No Bet', intl.formatMessage({ id: 'offer.no_bet' })),
  '12_456_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Correct Score', intl.formatMessage({ id: 'offer.period_correct_score' })),
  '12_443_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period Result', intl.formatMessage({ id: 'offer.period_result' })),
  '12_449_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Exact Goals', intl.formatMessage({ id: 'offer.period_exact_goals' })),
  '12_454_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Period', intl.formatMessage({ id: 'common.period' }))
      .replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '12_447_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Period', intl.formatMessage({ id: 'common.period' }))
      .replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '12_529_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Double Chance', intl.formatMessage({ id: 'offer.period_double_chance' })),
  '12_444_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period', intl.formatMessage({ id: 'common.period' })).replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '12_459_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Draw No Bet', intl.formatMessage({ id: 'offer.period_draw_no_bet' })),
  '12_455_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Period', intl.formatMessage({ id: 'common.period' }))
      .replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '12_462_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Odd/even', intl.formatMessage({ id: 'offer.period_odd_even' })),
  '12_450_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Period', intl.formatMessage({ id: 'common.period' }))
      .replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '12_32_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '12_184_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Goal & 1x2', intl.formatMessage({ id: 'offer.goal_and_1x2' })),
  '12_23_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '12_31_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '12_407_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Goal (incl. Overtime And Penalties)', intl.formatMessage({ id: 'offer.goal_incl_overtime_and_penalties' })),
  '4_665_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Day Session', intl.formatMessage({ id: 'offer.day_session' }))
      .replace('Total Runs', intl.formatMessage({ id: 'offer.total_runs' })),
  '4_675_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Top Batter', intl.formatMessage({ id: 'offer.top_batter' })),
  '4_678_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Last Player Standing', intl.formatMessage({ id: 'offer.last_player_standing' })),
  '4_674_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Top Batter', intl.formatMessage({ id: 'offer.top_batter' })),
  '4_677_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Top Bowler', intl.formatMessage({ id: 'offer.top_bowler' })),
  '5_14_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Handicap', intl.formatMessage({ id: 'offer.handicap' })),
  '1_31_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '1_32_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '1_54_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Highest Scoring Half', intl.formatMessage({ id: 'offer.highest_scoring_half' })),
  '1_50_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Either Half', intl.formatMessage({ id: 'offer.to_win_either_half' })),
  '1_49_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Both Halves', intl.formatMessage({ id: 'offer.to_win_both_halves' })),
  '1_56_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Score In Both Halves', intl.formatMessage({ id: 'offer.to_score_in_both_halves' })),
  '1_77_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '1_70_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '1_882_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Score (incl. Overtime)', intl.formatMessage({ id: 'offer.to_score_incl_overtime' })),
  '1_33_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Win To Nil', intl.formatMessage({ id: 'offer.win_to_nil' })),
  '1_24_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '1_105_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - 1x2 From', intl.formatMessage({ id: 'offer.10_minutes_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_28_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Odd/even', intl.formatMessage({ id: 'offer.odd_even' })),
  '1_57_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Score In Both Halves', intl.formatMessage({ id: 'offer.to_score_in_both_halves' })),
  '1_27_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Odd/even', intl.formatMessage({ id: 'offer.odd_even' })),
  '1_100_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('When Will The', intl.formatMessage({ id: 'offer.when_will_the' }))
      .replace('Goal Be Scored (15 Min Interval)', intl.formatMessage({ id: 'offer.goal_be_scored_15_min_interval' })),
  '1_41_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Correct Score', intl.formatMessage({ id: 'offer.correct_score' })),
  '1_76_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '1_48_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Both Halves', intl.formatMessage({ id: 'offer.to_win_both_halves' })),
  '1_879_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('To Win', intl.formatMessage({ id: 'common.to_win' })),
  '1_51_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Either Half', intl.formatMessage({ id: 'offer.to_win_either_half' })),
  '1_184_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Goal & Full Time Result', intl.formatMessage({ id: 'offer.goal_and_fulltime_result' })),
  '1_101_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('When Will The', intl.formatMessage({ id: 'offer.when_will_the' }))
      .replace('Goal Be Scored (10 Min Interval)', intl.formatMessage({ id: 'offer.goal_be_scored_10_min_interval' })),
  '1_34_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Win To Nil', intl.formatMessage({ id: 'offer.win_to_nil' })),
  '1_880_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('To Win', intl.formatMessage({ id: 'common.to_win' })),
  '1_8_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '1_53_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Highest Scoring Half', intl.formatMessage({ id: 'offer.highest_scoring_half' })),
  '1_69_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Goals', intl.formatMessage({ id: 'offer.total_goals' })),
  '1_23_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '1_62_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '1_163_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Corner', intl.formatMessage({ id: 'offer.corner' })),
  '1_174_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Corner', intl.formatMessage({ id: 'offer.corner' })),
  '1_181_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Exact Corners', intl.formatMessage({ id: 'offer.exact_corners' })),
  '1_141_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Bookings', intl.formatMessage({ id: 'offer.total_bookings' })),
  '1_596_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Booking 1x2 From', intl.formatMessage({ id: 'offer.5_minutes_booking_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_578_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Odd/even Corners From', intl.formatMessage({ id: 'offer.10_minutes_odd_even_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_140_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Bookings', intl.formatMessage({ id: 'offer.total_bookings' })),
  '1_569_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes', intl.formatMessage({ id: 'offer.15_minutes' }))
      .replace('Total Corners From', intl.formatMessage({ id: 'offer.total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_901_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Goalscorer', intl.formatMessage({ id: 'offer.goalscorer' })),
  '1_884_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1 Minute - Total Corners From', intl.formatMessage({ id: 'offer.1_minute_total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_883_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1 Minute - Total From', intl.formatMessage({ id: 'offer.1_minute_total_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_87_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('2nd Half - Handicap', intl.formatMessage({ id: 'offer.2nd_half_handicap' })),
  '1_601_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Race To', intl.formatMessage({ id: 'offer.race_to' }))
      .replace('Corners', intl.formatMessage({ id: 'common.corners' })),
  '1_592_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Total Bookings From', intl.formatMessage({ id: 'offer.10_minutes_total_bookings_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_574_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Corner Handicap From', intl.formatMessage({ id: 'offer.10_minutes_corner_handicap_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_575_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Total Corners From', intl.formatMessage({ id: 'offer.10_minutes_total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_568_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Total Corners From', intl.formatMessage({ id: 'offer.15_minutes_total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_602_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half - Race To', intl.formatMessage({ id: 'offer.1st_half_race_to' }))
      .replace('Corners', intl.formatMessage({ id: 'common.corners' })),
  '1_582_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Total Corners From', intl.formatMessage({ id: 'offer.5_minutes_total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_591_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Booking 1x2 From', intl.formatMessage({ id: 'offer.10_minutes_booking_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_103_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes', intl.formatMessage({ id: 'offer.15_minutes' }))
      .replace('Goal From', intl.formatMessage({ id: 'offer.goal_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_572_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Corner 1x2 From', intl.formatMessage({ id: 'offer.10_minutes_corner_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_885_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1 Minute - Total Bookings From', intl.formatMessage({ id: 'offer.1_minute_total_bookings_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_110_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Total From', intl.formatMessage({ id: 'offer.5_minutes_total_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_585_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Odd/even Corners From', intl.formatMessage({ id: 'offer.5_minutes_odd_even_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_580_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes', intl.formatMessage({ id: 'offer.5_minutes' }))
      .replace('Corner From', intl.formatMessage({ id: 'offer.corner_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_170_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Corner Range', intl.formatMessage({ id: 'offer.corner_range' })),
  '1_900_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Goalscorer', intl.formatMessage({ id: 'offer.goalscorer' })),
  '1_573_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes', intl.formatMessage({ id: 'offer.10_minutes' }))
      .replace('Corner From', intl.formatMessage({ id: 'offer.corner_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_179_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Corners', intl.formatMessage({ id: 'offer.total_corners' })),
  '1_600_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes', intl.formatMessage({ id: 'offer.5_minutes' }))
      .replace('Sending Off From', intl.formatMessage({ id: 'offer.sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_593_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Sending Off From', intl.formatMessage({ id: 'offer.10_minutes_sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_887_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1 Minute - Total Penalties Awarded From', intl.formatMessage({ id: 'offer.1_minute_total_penalties_awarded_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_577_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes', intl.formatMessage({ id: 'offer.10_minutes' }))
      .replace('Total Corners From', intl.formatMessage({ id: 'offer.total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_599_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes', intl.formatMessage({ id: 'offer.5_minutes' }))
      .replace('Sending Off From', intl.formatMessage({ id: 'offer.sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_107_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes - Total From', intl.formatMessage({ id: 'offer.10_minutes_total_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_581_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Corner Handicap From', intl.formatMessage({ id: 'offer.5_minutes_corner_handicap_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_104_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Total From', intl.formatMessage({ id: 'offer.15_minutes_total_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_80_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('1st Half - Correct Score', intl.formatMessage({ id: 'offer.1st_half_correct_score' })),
  '1_106_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes', intl.formatMessage({ id: 'offer.10_minutes' }))
      .replace('Goal From', intl.formatMessage({ id: 'offer.goal_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_570_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes', intl.formatMessage({ id: 'offer.15_minutes' }))
      .replace('Total Corners From', intl.formatMessage({ id: 'offer.total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_886_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1 Minute - Total Offsides From', intl.formatMessage({ id: 'offer.1_minute_total_offsides_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_896_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Scoring Type', intl.formatMessage({ id: 'offer.scoring_type' })),
  '1_583_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes', intl.formatMessage({ id: 'offer.5_minutes' }))
      .replace('Total Corners From', intl.formatMessage({ id: 'offer.total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_594_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes', intl.formatMessage({ id: 'offer.10_minutes' }))
      .replace('Sending Off From', intl.formatMessage({ id: 'offer.sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_168_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Corners', intl.formatMessage({ id: 'offer.total_corners' })),
  '1_153_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Bookings', intl.formatMessage({ id: 'offer.total_bookings' })),
  '1_167_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Corners', intl.formatMessage({ id: 'offer.total_corners' })),
  '1_595_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes', intl.formatMessage({ id: 'offer.10_minutes' }))
      .replace('Sending Off From', intl.formatMessage({ id: 'offer.sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_597_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Total Bookings From', intl.formatMessage({ id: 'offer.5_minutes_total_bookings_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_567_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Corner Handicap From', intl.formatMessage({ id: 'offer.15_minutes_corner_handicap_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_584_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes', intl.formatMessage({ id: 'offer.5_minutes' }))
      .replace('Total Corners From', intl.formatMessage({ id: 'offer.total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_108_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - 1x2 From', intl.formatMessage({ id: 'offer.5_minutes_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_579_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Corner 1x2 From', intl.formatMessage({ id: 'offer.5_minutes_corner_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_154_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Bookings', intl.formatMessage({ id: 'offer.total_bookings' })),
  '1_571_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Odd/even Corners From', intl.formatMessage({ id: 'offer.15_minutes_odd_even_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_576_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('10 Minutes', intl.formatMessage({ id: 'offer.10_minutes' }))
      .replace('Total Corners From', intl.formatMessage({ id: 'offer.total_corners_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_598_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes - Sending Off From', intl.formatMessage({ id: 'offer.5_minutes_sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_72_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '1_566_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes', intl.formatMessage({ id: 'offer.15_minutes' }))
      .replace('Corner From', intl.formatMessage({ id: 'offer.corner_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_109_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('5 Minutes', intl.formatMessage({ id: 'offer.5_minutes' }))
      .replace('Goal From', intl.formatMessage({ id: 'offer.goal_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_171_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Corner Range', intl.formatMessage({ id: 'offer.corner_range' })),
  '1_178_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Corners', intl.formatMessage({ id: 'offer.total_corners' })),
  '1_180_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Exact Corners', intl.formatMessage({ id: 'offer.exact_corners' })),
  '1_73_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Exact Goals', intl.formatMessage({ id: 'offer.exact_goals' })),
  '1_565_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Corner 1x2 From', intl.formatMessage({ id: 'offer.15_minutes_corner_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_102_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - 1x2 From', intl.formatMessage({ id: 'offer.15_minutes_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_586_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Booking 1x2 From', intl.formatMessage({ id: 'offer.15_minutes_booking_1x2_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_588_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Sending Off From', intl.formatMessage({ id: 'offer.15_minutes_sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_587_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes - Total Bookings From', intl.formatMessage({ id: 'offer.15_minutes_total_bookings_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_590_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes', intl.formatMessage({ id: 'offer.15_minutes' }))
      .replace('Sending Off From', intl.formatMessage({ id: 'offer.sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '1_589_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('15 Minutes', intl.formatMessage({ id: 'offer.15_minutes' }))
      .replace('Sending Off From', intl.formatMessage({ id: 'offer.sending_off_from' }))
      .replace('To', intl.formatMessage({ id: 'common.to' })),
  '4_679_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Last Player Standing', intl.formatMessage({ id: 'offer.last_player_standing' })),
  '4_676_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Top Bowler', intl.formatMessage({ id: 'offer.top_bowler' })),
  '1_150_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Booking', intl.formatMessage({ id: 'offer.booking' })),
  '1_143_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Exact Bookings', intl.formatMessage({ id: 'offer.exact_bookings' })),
  '1_156_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Exact Bookings', intl.formatMessage({ id: 'offer.exact_bookings' })),
  '1_137_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Booking', intl.formatMessage({ id: 'offer.booking' })),
  '1_144_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Exact Bookings', intl.formatMessage({ id: 'offer.exact_bookings' })),
  '9_508_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frame', intl.formatMessage({ id: 'offer.frame' }))
      .replace('Break 100+', intl.formatMessage({ id: 'offer.break_100_+' })),
  '9_500_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Point Handicap', intl.formatMessage({ id: 'offer.frame_point_handicap' })),
  '9_516_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Last Points Scored', intl.formatMessage({ id: 'offer.frame_last_points_scored' })),
  '9_513_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frame - Player To Pot', intl.formatMessage({ id: 'offer.frame_player_to_pot' }))
      .replace('Ball', intl.formatMessage({ id: 'offer.ball' })),
  '9_512_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Will There Be A Foul Committed', intl.formatMessage({ id: 'offer.frame_will_there_be_a_foul_committed' })),
  '9_499_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Winner', intl.formatMessage({ id: 'offer.frame_winner' })),
  '9_498_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frames 1 To', intl.formatMessage({ id: 'offer.frame_1_to' }))
      .replace('Result', intl.formatMessage({ id: 'offer.result' })),
  '9_497_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frames', intl.formatMessage({ id: 'offer.frames' }))
      .replace('To', intl.formatMessage({ id: 'common.to' }))
      .replace('Consecutive Frames Winner', intl.formatMessage({ id: 'offer.consecutive_frames_winner' })),
  '9_501_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Total Points', intl.formatMessage({ id: 'offer.frame_total_points' })),
  '9_515_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Player To Pot Last Ball', intl.formatMessage({ id: 'offer.frame_player_to_pot_last_ball' })),
  '9_502_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Odd/Even', intl.formatMessage({ id: 'offer.frame_odd_even' })),
  '9_510_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frame', intl.formatMessage({ id: 'offer.frame' }))
      .replace('Break 50+', intl.formatMessage({ id: 'offer.break_50_+' })),
  '9_496_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Race To', intl.formatMessage({ id: 'offer.race_to' }))
      .replace('Frames', intl.formatMessage({ id: 'offer.frames' })),
  '9_506_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Break 100+', intl.formatMessage({ id: 'offer.frame_break_100_+' })),
  '9_511_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frame', intl.formatMessage({ id: 'offer.frame' }))
      .replace('Break 50+', intl.formatMessage({ id: 'offer.break_50_+' })),
  '9_503_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frame - Race To', intl.formatMessage({ id: 'offer.frame_race_to' }))
      .replace('Points', intl.formatMessage({ id: 'common.points' })),
  '9_507_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frame', intl.formatMessage({ id: 'offer.frame' }))
      .replace('Break 100+', intl.formatMessage({ id: 'offer.break_100_+' })),
  '9_514_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frame', intl.formatMessage({ id: 'offer.frame' }))
      .replace('Colour Potted', intl.formatMessage({ id: 'offer.colour_potter' })),
  '9_504_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Player With Highest Break', intl.formatMessage({ id: 'offer.frame_player_with_highest_break' })),
  '9_505_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Highest Break', intl.formatMessage({ id: 'offer.frame_highest_break' })),
  '9_509_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Frame - Break 50+', intl.formatMessage({ id: 'offer.frame_break_50_+' })),
  '9_533_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Frames 1 To', intl.formatMessage({ id: 'offer.frame_1_to' }))
      .replace('Correct Score', intl.formatMessage({ id: 'offer.correct_score' })),
  '1_550_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Multigoals', intl.formatMessage({ id: 'offer.multigoals' })),
  '1_549_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Multigoals', intl.formatMessage({ id: 'offer.multigoals' })),
  '1_58_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Both Halves Over', intl.formatMessage({ id: 'offer.both_halves_over' })),
  '1_96_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('2nd Half', intl.formatMessage({ id: 'common.second_half' }))
      .replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '1_865_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Or Any Clean Sheet', intl.formatMessage({ id: 'offer.or_any_clean_sheet' })),
  '1_91_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('2nd Half', intl.formatMessage({ id: 'common.second_half' }))
      .replace('Total', intl.formatMessage({ id: 'common.total' })),
  '1_857_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Draw Or Under', intl.formatMessage({ id: 'offer.draw_or_under' })),
  '1_84_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('2nd Half', intl.formatMessage({ id: 'common.second_half' }))
      .replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '1_13_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('No Bet', intl.formatMessage({ id: 'offer.no_bet' })),
  '1_97_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('2nd Half', intl.formatMessage({ id: 'common.second_half' }))
      .replace('Clean Sheet', intl.formatMessage({ id: 'offer.clean_sheet' })),
  '1_12_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('No Bet', intl.formatMessage({ id: 'offer.no_bet' })),
  '1_860_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Or BTTS', intl.formatMessage({ id: 'offer.or_btts' })),
  '1_855_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Or Under', intl.formatMessage({ id: 'offer.or_under' })),
  '1_856_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Draw Or Over', intl.formatMessage({ id: 'offer.draw_or_over' })),
  '1_858_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Or Over', intl.formatMessage({ id: 'offer.or_over' })),
  '1_854_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Or Over', intl.formatMessage({ id: 'offer.or_over' })),
  '1_59_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Both Halves Under', intl.formatMessage({ id: 'offer.both_halves_under' })),
  '1_863_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Or Any Clean Sheet', intl.formatMessage({ id: 'offer.or_any_clean_sheet' })),
  '1_92_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('2nd Half', intl.formatMessage({ id: 'common.second_half' }))
      .replace('Total', intl.formatMessage({ id: 'common.total' })),
  '1_859_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Or Under', intl.formatMessage({ id: 'offer.or_under' })),
  '1_862_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Or BTTS', intl.formatMessage({ id: 'offer.or_btts' })),
  '1_157_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Exact Bookings', intl.formatMessage({ id: 'offer.exact_bookings' })),
  '1_147_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Sending Off', intl.formatMessage({ id: 'offer.sending_off' })),
  '1_148_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Sending Off', intl.formatMessage({ id: 'offer.sending_off' })),
  '4_700_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Inns - Any Player To Score', intl.formatMessage({ id: 'offer.inns_any_player_to_score' })),
  '4_701_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Any Player To Score', intl.formatMessage({ id: 'offer.any_player_to_score' })),
  '4_642_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Dismissal', intl.formatMessage({ id: 'offer.dismissal' })),
  '4_641_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns Over', intl.formatMessage({ id: 'offer.inns_over' }))
      .replace('Dismissal', intl.formatMessage({ id: 'offer.dismissal' })),
  '5_303_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Spread', intl.formatMessage({ id: 'offer.quarter_spread' })),
  '5_236_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Total Points', intl.formatMessage({ id: 'offer.quarter_total_points' })),
  '5_235_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter Result', intl.formatMessage({ id: 'offer.quarter_result' })),
  '4_669_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Run Outs', intl.formatMessage({ id: 'offer.total_run_outs' })),
  '4_668_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Run Outs', intl.formatMessage({ id: 'offer.total_run_outs' })),
  '3_203_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Game Handicap', intl.formatMessage({ id: 'offer.set_game_handicap' })),
  '3_190_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Games', intl.formatMessage({ id: 'offer.total_games' })),
  '3_192_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win A Set', intl.formatMessage({ id: 'offer.to_win_a_set' })),
  '3_193_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win A Set', intl.formatMessage({ id: 'offer.to_win_a_set' })),
  '3_191_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Games', intl.formatMessage({ id: 'offer.total_games' })),
  '4_706_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Ducks', intl.formatMessage({ id: 'offer.total_ducks' })),
  '4_707_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Inns', intl.formatMessage({ id: 'offer.inns' }))
      .replace('Total Ducks', intl.formatMessage({ id: 'offer.total_ducks' })),
  '3_206_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Will There Be A Tiebreak', intl.formatMessage({ id: 'offer.set_will_there_be_a_tiebreak' })),
  '3_209_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('And', intl.formatMessage({ id: 'offer.and' }))
      .replace('Game - Winner', intl.formatMessage({ id: 'offer.game_winner' })),
  '3_214_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game - Correct Score', intl.formatMessage({ id: 'offer.game_correct_score' })),
  '3_212_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game - To Deuce', intl.formatMessage({ id: 'offer.game_to_deuce' })),
  '3_208_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Race To', intl.formatMessage({ id: 'offer.race_to' }))
      .replace('Game', intl.formatMessage({ id: 'common.game' })),
  '3_211_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game - Exact Points', intl.formatMessage({ id: 'offer.game_exact_points' })),
  '3_216_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game', intl.formatMessage({ id: 'common.game' }))
      .replace('Race To', intl.formatMessage({ id: 'offer.race_to' }))
      .replace('Points', intl.formatMessage({ id: 'common.points' })),
  '3_217_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game', intl.formatMessage({ id: 'common.game' }))
      .replace('First', intl.formatMessage({ id: 'offer.first' }))
      .replace('Point - Winner', intl.formatMessage({ id: 'offer.point_winner' })),
  '3_205_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Odd/Even Games', intl.formatMessage({ id: 'offer.set_odd_even_games' })),
  '3_213_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Game - Odd/Even Points', intl.formatMessage({ id: 'offer.game_odd_even_points' })),
  '3_851_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Exactly 1 Set', intl.formatMessage({ id: 'offer.to_win_exactly_1_set' })),
  '3_850_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Exactly 1 Set', intl.formatMessage({ id: 'offer.to_win_exactly_1_set' })),
  '12_439_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Score In All Periods', intl.formatMessage({ id: 'offer.to_score_in_all_periods' })),
  '12_435_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win All Periods', intl.formatMessage({ id: 'offer.to_win_all_periods' })),
  '12_437_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Any Period', intl.formatMessage({ id: 'offer.to_win_any_period' })),
  '12_430_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Correct Score', intl.formatMessage({ id: 'offer.correct_score' })),
  '12_434_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Highest Scoring Period', intl.formatMessage({ id: 'offer.highest_scoring_period' })),
  '12_442_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('All Periods Under', intl.formatMessage({ id: 'offer.all_periods_under' })),
  '12_433_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Highest Scoring Period', intl.formatMessage({ id: 'offer.highest_scoring_period' })),
  '12_452_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Both Teams To Score', intl.formatMessage({ id: 'offer.period_both_teams_to_score' })),
  '12_441_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('All Periods Over', intl.formatMessage({ id: 'offer.all_periods_over' })),
  '12_453_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Which Team To Score', intl.formatMessage({ id: 'offer.period_which_team_to_score' })),
  '12_440_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Score In All Periods', intl.formatMessage({ id: 'offer.to_score_in_all_periods' })),
  '12_436_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win All Periods', intl.formatMessage({ id: 'offer.to_win_all_periods' })),
  '12_438_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Any Period', intl.formatMessage({ id: 'offer.to_win_any_period' })),
  '12_423_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace(
      'Goal & Winner (incl. Overtime And Penalties)',
      intl.formatMessage({ id: 'offer.goal_and_winner_incl_overtime_and_penalties' }),
    ),
  '12_408_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Handicap', intl.formatMessage({ id: 'offer.handicap' }))
      .replace('(incl. Overtime And Penalties)', intl.formatMessage({ id: 'offer.incl_overtime_and_penalties' })),
  '12_417_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace(
      'Exact Goals (incl. Overtime And Penalties)',
      intl.formatMessage({ id: 'offer.exact_goals_icl_overtime_and_penalties' }),
    ),
  '12_426_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace(
      'Period 1x2 & Winner (incl. Overtime And Penalties)',
      intl.formatMessage({ id: 'offer.period_1x2_and_winner_incl_overtime_an_penalties' }),
    ),
  '12_421_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace(
      'Clean Sheet (incl. Overtime And Penalties)',
      intl.formatMessage({ id: 'offer.clean_sheet_incl_overtime_and_penalties' }),
    ),
  '12_414_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total (incl. Overtime And Penalties)', intl.formatMessage({ id: 'offer.total_incl_overtime_and_penalties' })),
  '12_422_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace(
      'Clean Sheet (incl. Overtime And Penalties)',
      intl.formatMessage({ id: 'offer.clean_sheet_incl_overtime_and_penalties' }),
    ),
  '12_415_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total (incl. Overtime And Penalties)', intl.formatMessage({ id: 'offer.total_incl_overtime_and_penalties' })),
  '12_418_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace(
      'Exact Goals (incl. Overtime And Penalties)',
      intl.formatMessage({ id: 'offer.exact_goals_icl_overtime_and_penalties' }),
    ),
  '12_115_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Overtime', intl.formatMessage({ id: 'offer.overtime' })).replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '12_1060_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Scoring Type', intl.formatMessage({ id: 'offer.scoring_type' })),
  '12_1058_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Scoring Type', intl.formatMessage({ id: 'offer.scoring_type' })),
  '12_1059_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Scoring Type', intl.formatMessage({ id: 'offer.scoring_type' })),
  '12_457_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Period - Last Team To Score', intl.formatMessage({ id: 'offer.period_last_team_to_score' })),
  '6_236_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Total Points', intl.formatMessage({ id: 'offer.quarter_total_points' })),
  '6_228_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Points (incl. OT)', intl.formatMessage({ id: 'offer.total_points_incl_overtime' })),
  '6_69_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '6_227_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Points (incl. OT)', intl.formatMessage({ id: 'offer.total_points_incl_overtime' })),
  '6_70_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '6_230_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Race To', intl.formatMessage({ id: 'offer.race_to' }))
      .replace('Points (incl. OT)', intl.formatMessage({ id: 'offer.points_incl_ot' })),
  '6_235_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter Result', intl.formatMessage({ id: 'offer.quarter_result' })),
  '3_853_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Exactly 2 Sets', intl.formatMessage({ id: 'offer.to_win_exactly_2_sets' })),
  '3_852_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('To Win Exactly 2 Sets', intl.formatMessage({ id: 'offer.to_win_exactly_2_sets' })),
  '1_604_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Penalty Scored', intl.formatMessage({ id: 'offer.penalty_scored' })),
  '6_617_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Odd/even (incl. Overtime)', intl.formatMessage({ id: 'offer.odd_even_incl_overtime' })),
  '6_616_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Odd/even (incl. Overtime)', intl.formatMessage({ id: 'offer.odd_even_incl_overtime' })),
  '6_27_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Odd/even', intl.formatMessage({ id: 'offer.odd_even' })),
  '6_28_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Odd/even', intl.formatMessage({ id: 'offer.odd_even' })),
  '6_303_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Spread', intl.formatMessage({ id: 'offer.quarter_spread' })),
  '6_614_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Handicap (incl. Overtime)', intl.formatMessage({ id: 'offer.quarter_handicap_incl_overtime' })),
  '6_615_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Total (incl. Overtime)', intl.formatMessage({ id: 'offer.quarter_total_incl_overtime' })),
  '6_611_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - 1x2 (incl. Overtime)', intl.formatMessage({ id: 'offer.quarter_1x2_incl_overtime' })),
  '6_613_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Draw No Bet (incl. Overtime)', intl.formatMessage({ id: 'offer.quarter_draw_no_bet_incl_overtime' })),
  '6_302_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Quarter - Draw No Bet', intl.formatMessage({ id: 'offer.quarter_draw_no_bet' })),
  '6_920_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Receptions', intl.formatMessage({ id: 'offer.total_receptions' })),
  '6_919_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Receiving Yards', intl.formatMessage({ id: 'offer.total_receiving_yards' })),
  '6_915_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Pass Completions', intl.formatMessage({ id: 'offer.total_pass_completions' })),
  '6_914_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Passing Yards', intl.formatMessage({ id: 'offer.total_passing_yards' })),
  '6_918_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Rushing Yards', intl.formatMessage({ id: 'offer.total_rushing_yards' })),
  '7_563_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Race To', intl.formatMessage({ id: 'offer.race_to' }))
      .replace('Points', intl.formatMessage({ id: 'common.points' })),
  '7_19_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '7_20_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '7_480_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Tries', intl.formatMessage({ id: 'offer.total_tries' })),
  '7_479_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Tries', intl.formatMessage({ id: 'offer.total_tries' })),
  '7_467_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Scoring Play', intl.formatMessage({ id: 'offer.scoring_play' })),
  '7_69_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '7_70_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '7_489_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Tries', intl.formatMessage({ id: 'offer.total_tries' })),
  '7_488_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half', intl.formatMessage({ id: 'common.first_half' }))
      .replace('Total Tries', intl.formatMessage({ id: 'offer.total_tries' })),
  '5_228_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Points (incl. OT)', intl.formatMessage({ id: 'offer.total_points_incl_overtime' })),
  '5_227_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Points(incl. OT)', intl.formatMessage({ id: 'offer.total_points_incl_overtime' })),
  '6_940_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Field Goal Made (incl. OT)', intl.formatMessage({ id: 'offer.field_goal_made_incl_ot' })),
  '1_115_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Extra Time', intl.formatMessage({ id: 'offer.extra_time' }))
      .replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '6_959_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Drive - Result', intl.formatMessage({ id: 'offer.drive_result' })),
  '8_382_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Player To Score A 180', intl.formatMessage({ id: 'offer.player_to_score_a_180' })),
  '4_709_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Player Performance', intl.formatMessage({ id: 'offer.total_player_performance' })),
  '5_230_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Race To', intl.formatMessage({ id: 'offer.race_to' }))
      .replace('Points (incl. Overtime)', intl.formatMessage({ id: 'offer.points_incl_overtime' })),
  '5_756_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Quarter', intl.formatMessage({ id: 'common.quarter' }))
      .replace('Total', intl.formatMessage({ id: 'common.total' })),
  '5_757_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Quarter', intl.formatMessage({ id: 'common.quarter' }))
      .replace('Total', intl.formatMessage({ id: 'common.total' })),
  '1_124_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Penalty Shootout', intl.formatMessage({ id: 'offer.penalty_shootout' }))
      .replace('Penalty Scored', intl.formatMessage({ id: 'offer.penalty_scored' })),
  '1_125_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Penalty Shootout', intl.formatMessage({ id: 'offer.penalty_shootout' }))
      .replace('Goal', intl.formatMessage({ id: 'offer.goal' })),
  '5_1088_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Scoring Type', intl.formatMessage({ id: 'offer.scoring_type' }))
      .replace('Point (incl. Overtime)', intl.formatMessage({ id: 'offer.point_incl_overtime' })),
  '5_1089_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Any Team Scoring Type', intl.formatMessage({ id: 'offer.any_team_scoring_type' }))
      .replace('Point (incl. Overtime)', intl.formatMessage({ id: 'offer.point_incl_overtime' })),
  '6_1064_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Drive Play', intl.formatMessage({ id: 'offer.drive_play' }))
      .replace('New First Down', intl.formatMessage({ id: 'offer.new_first_down' })),
  '6_1066_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Drive', intl.formatMessage({ id: 'offer.drive' })).replace('Result', intl.formatMessage({ id: 'offer.result' })),
  '6_1062_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Drive Play', intl.formatMessage({ id: 'offer.drive_play' }))
      .replace('Pass Completion', intl.formatMessage({ id: 'offer.pass_completion' })),
  '6_1065_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Drive Play', intl.formatMessage({ id: 'offer.drive_play' }))
      .replace('Sack', intl.formatMessage({ id: 'offer.sack' })),
  '6_1063_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Drive Play', intl.formatMessage({ id: 'offer.drive_play' }))
      .replace('Total Yards Gained', intl.formatMessage({ id: 'offer.total_yards_gained' })),
  '6_1061_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Drive Play', intl.formatMessage({ id: 'offer.drive_play' }))
      .replace('Play Type', intl.formatMessage({ id: 'offer.play_type' })),
  '6_909_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Touchdown Scorer (incl. Overtime)', intl.formatMessage({ id: 'offer.touchdown_scorer_incl_overtime' })),
  '8_202_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Set - Winner', intl.formatMessage({ id: 'offer.set_winner' })),
  '8_389_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Total 180s', intl.formatMessage({ id: 'offer.total_180s' })),
  '8_390_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Total 180s', intl.formatMessage({ id: 'offer.total_180s' })),
  '8_387_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Most 180s', intl.formatMessage({ id: 'offer.set_most_180s' })),
  '8_388_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Set - Total 180s', intl.formatMessage({ id: 'offer.set_total_180s' })),
  '7_472_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('1st Half - Race To', intl.formatMessage({ id: 'offer.1st_half_race_to' }))
      .replace('Points', intl.formatMessage({ id: 'common.points' })),
  '14_245_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Game - Winner', intl.formatMessage({ id: 'offer.game_winner' })),
  '14_247_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Game', intl.formatMessage({ id: 'common.game' }))
      .replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '14_246_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Game', intl.formatMessage({ id: 'common.game' }))
      .replace('Point Handicap', intl.formatMessage({ id: 'offer.point_handicap' })),
  '14_520_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Game', intl.formatMessage({ id: 'common.game' })).replace('Point', intl.formatMessage({ id: 'offer.point' })),
  '14_250_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Game - Race To', intl.formatMessage({ id: 'offer.game_race_to' }))
      .replace('Points', intl.formatMessage({ id: 'common.points' })),
  '16_202_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Set - Winner', intl.formatMessage({ id: 'offer.set_winner' })),
  '16_309_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Point Handicap', intl.formatMessage({ id: 'offer.point_handicap' })),
  '16_310_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Set', intl.formatMessage({ id: 'common.set' }))
      .replace('Total Points', intl.formatMessage({ id: 'offer.total_points' })),
  '18_1095_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Pit Stop', intl.formatMessage({ id: 'offer.pit_stop' })),
  '18_1094_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('To Retire', intl.formatMessage({ id: 'offer.to_retire' })),
  '18_1102_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Podium', intl.formatMessage({ id: 'offer.podium' }))
      .replace('teams', intl.formatMessage({ id: 'offer.teams' }).toLowerCase()),
  '18_1107_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Laps', intl.formatMessage({ id: 'offer.laps' }))
      .replace('To', intl.formatMessage({ id: 'common.to' }))
      .replace('Fastest Lap', intl.formatMessage({ id: 'offer.fastest_lap' })),
  '18_1108_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Lap', intl.formatMessage({ id: 'common.lap' }))
      .replace('Total Retirements', intl.formatMessage({ id: 'offer.total_retirements' })),
  '18_1105_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Pit Stop', intl.formatMessage({ id: 'offer.pit_stop' }))
      .replace('teams', intl.formatMessage({ id: 'offer.teams' }).toLowerCase()),
  '18_1104_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('To Retire', intl.formatMessage({ id: 'offer.to_retire' }))
      .replace('teams', intl.formatMessage({ id: 'offer.teams' }).toLowerCase()),
  '18_1109_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Lap', intl.formatMessage({ id: 'common.lap' }))
      .replace('Fastest Lap', intl.formatMessage({ id: 'offer.fastest_lap' })),
  '18_1103_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Head2head', intl.formatMessage({ id: 'offer.head2head' }))
      .replace('teams', intl.formatMessage({ id: 'offer.teams' }).toLowerCase()),
  '18_1101_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Winner', intl.formatMessage({ id: 'common.winner' }))
      .replace('teams', intl.formatMessage({ id: 'offer.teams' }).toLowerCase()),
  '17_538_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Head2head', intl.formatMessage({ id: 'offer.head2head' })),
  '17_1023_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Hole', intl.formatMessage({ id: 'common.hole' })).replace('2 ball', intl.formatMessage({ id: 'offer.ball_2' })),
  '17_1022_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Hole', intl.formatMessage({ id: 'common.hole' })).replace('3 Ball', intl.formatMessage({ id: 'offer.ball_3' })),
  '17_1006_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('2 Ball', intl.formatMessage({ id: 'offer.ball_2' })),
  '17_1007_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('2 Ball', intl.formatMessage({ id: 'offer.ball_2' }))
      .replace('handicap', intl.formatMessage({ id: 'offer.handicap' }).toLowerCase()),
  '17_1028_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Holes', intl.formatMessage({ id: 'offer.holes' }))
      .replace('To', intl.formatMessage({ id: 'common.to' }))
      .replace('2 Ball', intl.formatMessage({ id: 'offer.ball_2' })),
  '17_1013_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Eagles', intl.formatMessage({ id: 'offer.total_eagles' })),
  '17_1010_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Birdies', intl.formatMessage({ id: 'offer.total_birdies' })),
  '17_1008_2': (intl: IntlShape, outcomeName: string) => outcomeName.replace('Total Pars', intl.formatMessage({ id: 'offer.total_pars' })),
  '17_1011_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Strokes', intl.formatMessage({ id: 'offer.total_strokes' })),
  '17_1012_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total Bogeys', intl.formatMessage({ id: 'offer.total_bogeys' })),
  '17_1025_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName
      .replace('Hole', intl.formatMessage({ id: 'common.hole' }))
      .replace('Any Player Under Par', intl.formatMessage({ id: 'offer.any_player_under_par' })),
  '17_1021_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Odd/even Strokes', intl.formatMessage({ id: 'offer.odd_even_strokes' })),
  '17_1014_2': (intl: IntlShape, outcomeName: string) =>
    outcomeName.replace('Total 2+ Over Par', intl.formatMessage({ id: 'offer.total_2_plus_over_par' })),
};

export const offerWildCards: OffersTranslations = {
  ...outrightOfferWildCards,
  ...simpleOfferWildCards,
  ...complexOfferWildCards,
};
